import classnames from 'classnames';
import useAuth from '../../hooks/global/useAuth';
import { useTranslation } from 'react-i18next';
import useAppMethods from '../../hooks/utility/useAppMethods';
import React from 'react';
import useScreenType from '../../hooks/utility/useScreenType';
import useCashbackProgram from '../../hooks/cashback/useCashbackProgram';
import { LogOut, Star } from 'lucide-react';
import { Dialog } from '@mui/material';
import useRouter from '../../hooks/navigation/useRouter';
import { SCREENS } from '../../services/exports/Constants';

interface Props {
  className?: string;
}

export default function UserBadge(props: Props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Profile:UserBadge' });
  const { className } = props;
  const { isLoggedIn, logout } = useAuth();
  const { toggleLoginModal } = useAppMethods();
  const { isMobile } = useScreenType();
  const { hasCashbackProgram, balance } = useCashbackProgram();
  const { navigate } = useRouter();
  const [showLogoutConfirm, setShowLogoutConfirm] = React.useState(false);

  const openLogin = () => {
    toggleLoginModal({
      openModal: true,
      onCloseRedirect: () => null,
    });
  };

  const handleLogout = () => {
    setShowLogoutConfirm(false);
    logout();
  };

  if (!isLoggedIn) {
    return (
      <div className={classnames('flex items-center gap-x-xs sm:gap-md', className)}>
        {!isMobile && (
          <button onClick={openLogin} className={signInButton}>
            {t('buttons.sign_in')}
          </button>
        )}
        <button onClick={openLogin} className={signUpButton}>
          {t('buttons.sign_up')}
        </button>
      </div>
    );
  }

  const goToRewards = () => navigate({ web: { to: SCREENS.REWARDS_SCREEN } });

  return (
    <div className={classnames('flex items-center gap-x-md', className)}>
      {!isMobile && hasCashbackProgram && (
        <button onClick={goToRewards} className={pointsText}>
          <Star className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-current" />
          {balance}
          {' pts'}
        </button>
      )}
      <button onClick={() => setShowLogoutConfirm(true)} className={logoutIconButton} title={t('buttons.logout')}>
        <LogOut className="w-4 h-4" />
      </button>

      {/* Logout confirmation modal */}
      <Dialog
        open={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        slotProps={{
          paper: { className: '!rounded-xl max-w-sm w-full mx-md' },
        }}
      >
        <div className="p-lg text-center">
          <div className={logoutModalIcon}>
            <LogOut className="w-5 h-5 text-red-500" />
          </div>
          <h3 className="text-md font-bold text-brand-text-primary">{t('logout_confirm.title')}</h3>
          <p className="text-sm text-muted-foreground mt-xs">{t('logout_confirm.description')}</p>
          <div className="flex gap-sm mt-lg">
            <button onClick={() => setShowLogoutConfirm(false)} className={cancelButton}>
              {t('logout_confirm.cancel')}
            </button>
            <button onClick={handleLogout} className={confirmLogoutButton}>
              {t('logout_confirm.confirm')}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

const signInButton =
  'hidden sm:block text-sm font-medium text-brand-text-primary hover:text-brand-primary transition-colors';
const signUpButton =
  'text-xs sm:text-sm font-semibold bg-brand-button-primary-background text-brand-button-primary-text rounded-full px-sm sm:px-md py-xs hover:opacity-90 transition-opacity whitespace-nowrap';
const pointsText =
  'flex items-center gap-1.5 text-brand-primary text-xs sm:text-sm font-semibold hover:opacity-80 transition-opacity';
const logoutIconButton =
  'text-muted-foreground hover:text-brand-text-primary rounded-full w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center transition-colors';
const logoutModalIcon =
  'mx-auto w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center mb-sm';
const cancelButton =
  'flex-1 rounded-full h-11 border border-brand-border text-sm font-medium text-brand-text-primary hover:bg-brand-background-hover transition-colors';
const confirmLogoutButton =
  'flex-1 rounded-full h-11 bg-brand-button-primary-background text-brand-button-primary-text text-sm font-medium hover:opacity-90 transition-opacity';
